import React, { useContext, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Layout } from '../styles';
import appRoutes, {
  buildShowWallBoard,
  PRINT_WALLBOARD_PREFIX,
} from '../appRoutes';
import AccountContext from '../../contexts/AccountContext';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import { SLIDE_OUT_ELEMENT_ID, FADE_IN_ELEMENT_ID } from '../../constants';
import Loading from '../../components/Loading';
import FlexCentered from '../../components/Common/FlexCentered';
import FleetOpsSuspense from '../../FleetOpsSuspense';
import MainNav from '../../kingpin/navigation/MainNav';
import MaintenanceBanner from '../../components/MaintenanceBanner';

import OnBoardingSwitch from '../Onboarding/OnboardingSwitch';
import PrintReportShow from '../../screens/PrintReportShow';
import PrintDashboardShow from '../../screens/PrintDashboardShow';
import PrintWallboardShow from '../../screens/PrintWallboardShow';
import BonusRules from '../../screens/BonusRules';
import { PortalsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import LegacyAuthenticatedSwitch from './LegacyAuthenticatedSwitch';

import { CurrentUserContentSettingsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/CurrentUserContentSettingsProvider';
import EngagementModeSwitch from './V2/EngagementModeSwitch';
import PortalModeSwitch from './V2/PortalModeSwitch';
import { UIVersionContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import V3 from './V3';

const Sidebar = () => (
  <Routes>
    <Route path={appRoutes.loggedIn.showWallBoard} element={null} />
    <Route path={`/admin${appRoutes.loggedIn.showWallBoard}`} element={null} />
    {/* Studio is a full-width builder, with or without a portal's slug */}
    <Route path={`${appRoutes.loggedIn.studio}/*`} element={null} />
    <Route path={`/:portal${appRoutes.loggedIn.studio}/*`} element={null} />
    <Route path="*" element={<MainNav />} />
  </Routes>
);

const AuthenticatedSwitch = () => {
  const { isPortalsEnabled } = useContext(PortalsContext);
  const { contentSettings } = useContext(CurrentUserContentSettingsContext);

  if (isPortalsEnabled) {
    if (contentSettings.mode === 'engagement') {
      return (
        <EngagementModeSwitch engagementContentSettings={contentSettings} />
      );
    }

    return <PortalModeSwitch />;
  }

  return <LegacyAuthenticatedSwitch />;
};

// A column so the maintenance banner can sit above the layout, with the layout
// taking the remaining height (identical to before when there's no banner).
const V1V2Root = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const V1V2UI = () => (
  <V1V2Root>
    <MaintenanceBanner />
    <Layout style={{ height: 'auto', flex: 1, minHeight: 0 }}>
      <Routes>
        <Route
          path={'*'}
          element={
            <>
              <Sidebar />
              <FleetOpsSuspense>
                <AuthenticatedSwitch />
              </FleetOpsSuspense>
              <div id={SLIDE_OUT_ELEMENT_ID} />
              <div id={FADE_IN_ELEMENT_ID} />
            </>
          }
        />
      </Routes>
    </Layout>
  </V1V2Root>
);

const AuthenticatedGate = () => {
  const { uiVersion } = useContext(UIVersionContext);
  const { status } = useContext(AccountContext);
  const { isWallboardUser, wallBoardId, isFleetOpsStaff } =
    useContext(CurrentUserContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // The headless scheduled-report renderer authenticates as a wallboard user
    // but must stay on /reports/:id to render the report — don't force it to the
    // wallboard screen. It's identified by the renderToken param.
    const isHeadlessRender = new URLSearchParams(location.search).has(
      'renderToken',
    );
    if (isWallboardUser && wallBoardId && !isHeadlessRender) {
      if (location.pathname !== buildShowWallBoard(wallBoardId, undefined)) {
        navigate(buildShowWallBoard(wallBoardId, undefined));
      }
    }
  }, [
    isWallboardUser,
    location.pathname,
    location.search,
    navigate,
    wallBoardId,
  ]);

  if (!status) {
    return (
      <div style={{ height: '100vh' }}>
        <FlexCentered style={{ height: '100%' }}>
          <Loading />
        </FlexCentered>
      </div>
    );
  }

  // Headless scheduled-report render: chrome-less report, no nav/version switch.
  // Mounted here so it sits inside the SplashScreen data providers but above the
  // version-specific layouts that add the nav.
  if (location.pathname.startsWith('/print/reports/')) {
    return <PrintReportShow />;
  }

  // Headless scheduled-dashboard render: same chrome-less treatment as reports.
  if (location.pathname.startsWith('/print/dashboards/')) {
    return <PrintDashboardShow />;
  }

  // Headless wallboard screenshot render: one slide at a time, chrome-less.
  if (location.pathname.startsWith(PRINT_WALLBOARD_PREFIX)) {
    return <PrintWallboardShow />;
  }

  // Bonus program rules, opened in a new tab: chrome-less like the print renders.
  if (location.pathname.startsWith('/bonus-rules/')) {
    return <BonusRules />;
  }

  if (status !== 'active' && !isFleetOpsStaff) {
    return <OnBoardingSwitch />;
  }

  if (uiVersion === 'v3') {
    return <V3.Standard />;
  }

  return <V1V2UI />;
};

export default AuthenticatedGate;
